import { Callable, TimeoutId } from '@prophecy/interfaces/generic';
import { toast } from '@prophecy/ui-v3/LegacyToast';
import { ToastUtilReturnType } from '@prophecy/ui-v3/LegacyToast/types';

const PING_INTERVAL = 10 * 1000; // 10 sec
const BUFFER = 20 * 1000; // 20 sec
const ONE_MB_IN_BYTES = 1024 * 1024;
const TEN_MB_IN_BYTES = 10 * ONE_MB_IN_BYTES;
const TIMEOUT_BUFFER_PER_MB = 1000; // 1 sec

export const WS_TIMEOUT_INTERVAL = 3 * 60 * 1000; // 3 minutes

export class KeepAliveClient {
  private _retryToastTimeout?: TimeoutId;

  private _retryToast?: ToastUtilReturnType;

  private _showToast?: boolean;

  private handlePong: Callable;

  public preventDisconnect?: boolean;

  private closeConnection: () => void;

  private pingTimeoutInterval: number = PING_INTERVAL + BUFFER;

  private lastTimeoutStartedAt: number = 0;

  constructor(options: { closeConnection: () => void; handlePong: () => void }) {
    this.closeConnection = options.closeConnection;
    this.handlePong = options.handlePong;
  }

  private clearToast() {
    this.lastTimeoutStartedAt = 0;
    if (this._retryToast) {
      this._retryToast.close();
    }
    if (this._retryToastTimeout) clearTimeout(this._retryToastTimeout);
  }

  public handlePingTimeout() {
    this.clearToast();

    /**
     * This is to handle the case where backend is closed, but from the frontend perspective the
     * socket is still up. In this case ping will not come from backend.
     * The socket timeout after 1-2 min. so in between user will see this toast
     * So we proactively close the connection if we don't recieve a ping from the backend for on specific timeout (30s)
     */
    this.lastTimeoutStartedAt = Date.now();
    this._retryToastTimeout = setTimeout(() => {
      this.closeConnection();

      if (!document.hidden && this._showToast) {
        this._retryToast = toast.loading({
          content: 'Retrying to connect IDE.',
          placement: 'bottom',
          closeable: false
        });
      }
    }, this.pingTimeoutInterval);
  }

  private visibilityChange = () => {
    // we need to refresh token when ever there visibility change visible -> hidden or hidden to active.
    // visible -> hidden is required so that we make sure, token is not expired just after the tab change
    // hidden -> visible is required so that we can check if token is valid
    this.sendPong();
  };

  private sendPong() {
    this.handlePong();
  }

  public init(showToast = true) {
    this._showToast = showToast;
    document.addEventListener('visibilitychange', this.visibilityChange);
  }

  public setPingTimeoutInterval(delay: number) {
    this.pingTimeoutInterval = delay;
    this.handlePingTimeout();
  }

  public setPingTimeoutIntervalBasedOnResSize(size: number) {
    if (size <= TEN_MB_IN_BYTES) return;

    const additionalBuffer = Math.floor((size - TEN_MB_IN_BYTES) / ONE_MB_IN_BYTES) * TIMEOUT_BUFFER_PER_MB;
    if (additionalBuffer <= 0) return;

    this.setPingTimeoutInterval(Math.min(PING_INTERVAL + BUFFER + additionalBuffer, WS_TIMEOUT_INTERVAL));
  }

  // for messages from frontend, we shouldn't fully reset the timeout instead add on the current pending timeout based on the size of the message
  public setPingTimeoutIntervalBasedOnReqSize(size: number) {
    const additionalBuffer = Math.floor(size / ONE_MB_IN_BYTES) * TIMEOUT_BUFFER_PER_MB;

    if (this.lastTimeoutStartedAt && additionalBuffer > 0) {
      const pendingTimeout = Math.max(0, this.pingTimeoutInterval - (Date.now() - this.lastTimeoutStartedAt));
      this.setPingTimeoutInterval(Math.min(pendingTimeout + additionalBuffer, WS_TIMEOUT_INTERVAL));
    }
  }

  public onPing() {
    this.onMessage();

    if (!document.hidden || this.preventDisconnect) {
      this.sendPong();
    }
  }

  public onMessage() {
    this.pingTimeoutInterval = PING_INTERVAL + BUFFER;

    this.handlePingTimeout();
  }

  public onClose() {
    document.removeEventListener('visibilitychange', this.visibilityChange);

    this.clearToast();
  }
}
